import axios, {
  type AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import { API_CONFIG } from '@/config/api';
import { ROUTES } from '@/lib/constants/routes';
import { removeEmptyFields } from '@/lib/utils/object';

// Extend AxiosRequestConfig to include retry flag
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseUrl,
  timeout: API_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null,
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Attach bearer token if exists
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Clean empty fields from request body for mutation methods
    const method = config.method?.toLowerCase();
    if (
      (method === 'post' || method === 'put' || method === 'patch') &&
      config.data &&
      typeof config.data === 'object' &&
      !Array.isArray(config.data) &&
      !(config.data instanceof FormData)
    ) {
      config.data = removeEmptyFields(config.data);
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Process matter responses to ensure filePrefix exists
    const method = (response.config.method ?? '').toLowerCase();
    const path = extractPath(response.config.url, response.config.baseURL);

    // LIST: /matters
    if (method === 'get' && /\/matters\/?$/.test(path) && Array.isArray(response.data?.results)) {
      response.data.results = response.data.results.map((item: Record<string, unknown>) => ({
        ...item,
        filePrefix: item['filePrefix'] ?? buildMatterFilePrefix(item),
      }));
    }

    // DETAIL: /matters/:id
    if (method === 'get' && /\/matters\/[^/]+$/.test(path) && response.data) {
      response.data.filePrefix = response.data.filePrefix ?? buildMatterFilePrefix(response.data);
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig | undefined;

    // Handle 401 Unauthorized - attempt token refresh (skip for login requests)
    const requestUrl = originalRequest?.url ?? '';
    if (
      error.response?.status === 401 &&
      (error.response.data as { message?: string })?.message === 'Unauthorized' &&
      originalRequest &&
      !originalRequest._retry &&
      !requestUrl.includes('/auth/login')
    ) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await axios.put<{ access: string }>(
          `${API_CONFIG.baseUrl}/auth/renew-access`,
          { refresh: refreshToken }
        );

        const newAccessToken = response.data.access;
        localStorage.setItem('accessToken', newAccessToken);

        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return apiClient(originalRequest);
      } catch {
        // Refresh failed - clear tokens and redirect to login
        clearAuthData();
        window.location.href = ROUTES.LOGIN;
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

// Helper: Extract path from URL
function extractPath(url: string | undefined, baseURL: string | undefined): string {
  try {
    const fullUrl = new URL(url ?? '', baseURL ?? window.location.origin);
    return fullUrl.pathname;
  } catch {
    return url ?? '';
  }
}

// Helper: Build matter file prefix
function buildMatterFilePrefix(item: Record<string, unknown>): string {
  const practiceArea = item['practiceArea'] as { prefix?: string } | undefined;
  const prefix = practiceArea?.prefix ?? (item['prefix'] as string | undefined) ?? '';
  const createdAt = item['createdAt'] as string | undefined;
  const year = createdAt ? new Date(createdAt).getFullYear() : '';
  const id = (item['id'] ?? item['_id'] ?? '') as string | number;
  return [prefix, year, id].filter(Boolean).join('-');
}

// Helper: Clear all auth data
function clearAuthData(): void {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  document.cookie = 'role=; Max-Age=0; path=/';
}

// Type-safe request methods
export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: unknown, config?: AxiosRequestConfig) =>
    apiClient.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: AxiosRequestConfig) =>
    apiClient.delete<T>(url, config).then((res) => res.data),
};

export default apiClient;
